import React, { useState, useEffect } from 'react';
import { useUserStore } from '../../../../store/userStore';
import { API_BASE_URL } from '../../../../config/api';
import {
  BuildingOfficeIcon,
  ChartBarIcon,
  CogIcon,
  DocumentTextIcon,
  BellIcon,
  UsersIcon,
  PresentationChartLineIcon,
  QuestionMarkCircleIcon,
  CreditCardIcon,
} from '@heroicons/react/24/outline';

const AdminDashboard: React.FC = () => {
  const { currentUser } = useUserStore();
  const [loading, setLoading] = useState(true);
  const [dashboardStats, setDashboardStats] = useState<any>(null);
  const [managementStats, setManagementStats] = useState<any>(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      // 대시보드 통계 로드
      const statsResponse = await fetch(`${API_BASE_URL}/v1/admin/dashboard/stats`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
          'Content-Type': 'application/json',
        },
      });

      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        setDashboardStats(statsData);
      }

      // 관리 영역 통계 로드
      const mgmtResponse = await fetch(`${API_BASE_URL}/v1/admin/dashboard/management-areas`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
          'Content-Type': 'application/json',
        },
      });

      if (mgmtResponse.ok) {
        const mgmtData = await mgmtResponse.json();
        setManagementStats(mgmtData);
      }

      setLoading(false);
    } catch (error) {
      console.error('대시보드 데이터 로드 실패:', error);
      setLoading(false);
    }
  };

  const formatRevenue = (amount: number) => {
    if (amount >= 10000000) {
      return `₩${Math.floor(amount / 10000)}만`;
    } else if (amount >= 10000) {
      return `₩${Math.floor(amount / 10000)}만`;
    }
    return `₩${amount.toLocaleString()}`;
  };

  const stats = dashboardStats ? [
    {
      name: '등록된 오너',
      value: dashboardStats.owners.total.toString(),
      change: `+${dashboardStats.owners.increase}`,
      changeType: dashboardStats.owners.changeType,
      icon: UsersIcon,
    },
    {
      name: '운영 시설',
      value: dashboardStats.facilities.total.toString(),
      change: `+${dashboardStats.facilities.increase}`,
      changeType: dashboardStats.facilities.changeType,
      icon: BuildingOfficeIcon,
    },
    {
      name: '월 수수료',
      value: formatRevenue(dashboardStats.monthly_revenue.total),
      change: `+${dashboardStats.monthly_revenue.increase_percent}%`,
      changeType: dashboardStats.monthly_revenue.changeType,
      icon: ChartBarIcon,
    },
    {
      name: '대기 문의',
      value: dashboardStats.pending_contacts.total.toString(),
      change: `+${dashboardStats.pending_contacts.increase}`,
      changeType: dashboardStats.pending_contacts.changeType,
      icon: QuestionMarkCircleIcon,
    },
  ] : [];

  const managementAreas = managementStats ? [
    {
      title: '고객 지원',
      description: '문의 관리 및 고객 상담 서비스',
      icon: DocumentTextIcon,
      href: '/admin/dashboard/contacts',
      color: 'bg-blue-500',
      stats: `${managementStats.contacts.pending}${managementStats.contacts.unit}`,
    },
    {
      title: '오너 관리',
      description: '사업자 등록 및 시설 운영자 관리',
      icon: UsersIcon,
      href: '/admin/dashboard/owners',
      color: 'bg-purple-500',
      stats: `${managementStats.owners.total}${managementStats.owners.unit}`,
    },
    {
      title: '시설 관리',
      description: '전체 시설 현황 및 운영 상태 모니터링',
      icon: BuildingOfficeIcon,
      href: '/admin/dashboard/facilities',
      color: 'bg-green-500',
      stats: `${managementStats.facilities.total}${managementStats.facilities.unit}`,
    },
    {
      title: '통계 분석',
      description: '서비스 이용 현황 및 매출 분석',
      icon: PresentationChartLineIcon,
      href: '/admin/dashboard/analytics',
      color: 'bg-indigo-500',
      stats: `월 ${formatRevenue(managementStats.analytics.monthly_revenue)}`,
    },
    {
      title: '시스템 관리',
      description: '서버 상태, 보안 및 시스템 설정',
      icon: CogIcon,
      href: '/admin/dashboard/system',
      color: 'bg-gray-500',
      stats: managementStats.system.status,
    },
    {
      title: '알림 센터',
      description: '시스템 알림 및 공지사항 관리',
      icon: BellIcon,
      href: '/admin/dashboard/notifications',
      color: 'bg-yellow-500',
      stats: `${managementStats.notifications.scheduled}${managementStats.notifications.unit}`,
    },
    {
      title: '서비스 플랜',
      description: '독서실 오너용 서비스 플랜 관리',
      icon: CreditCardIcon,
      href: '/admin/dashboard/service-plans',
      color: 'bg-orange-500',
      stats: '4개 플랜',
    },
  ] : [];

  const recentActivities = [
    {
      id: 1,
      type: 'inquiry',
      message: '새로운 문의가 접수되었습니다 - (주)스마트스터디',
      time: '5분 전',
      status: 'new',
    },
    {
      id: 2,
      type: 'owner',
      message: '새로운 오너가 가입승인을 요청했습니다 - 김오너',
      time: '1시간 전',
      status: 'new',
    },
    {
      id: 3,
      type: 'facility',
      message: '강남 독서실 A 시설이 등록되었습니다',
      time: '2시간 전',
      status: 'completed',
    },
    {
      id: 4,
      type: 'system',
      message: '시스템 백업이 완료되었습니다',
      time: '6시간 전',
      status: 'completed',
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* 헤더 */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">
          어텐드 관리자 대시보드
        </h1>
        <p className="mt-2 text-gray-600">
          안녕하세요, {currentUser?.name}님. 어텐드 전체 서비스를 관리해보세요.
        </p>
      </div>

      {/* 통계 카드 */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8">
        {stats.map((item) => (
          <div
            key={item.name}
            className="bg-white overflow-hidden shadow rounded-lg"
          >
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <item.icon
                    className="h-6 w-6 text-gray-400"
                    aria-hidden="true"
                  />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      {item.name}
                    </dt>
                    <dd className="flex items-baseline">
                      <div className="text-2xl font-semibold text-gray-900">
                        {item.value}
                      </div>
                      <div
                        className={`ml-2 flex items-baseline text-sm font-semibold ${
                          item.changeType === 'increase'
                            ? 'text-green-600'
                            : 'text-red-600'
                        }`}
                      >
                        {item.change}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* 관리 영역 */}
        <div className="lg:col-span-2">
          <h2 className="text-lg font-medium text-gray-900 mb-4">관리 영역</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {managementAreas.map((area) => (
              <div
                key={area.title}
                className="bg-white overflow-hidden shadow rounded-lg hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => (window.location.href = area.href)}
              >
                <div className="p-6">
                  <div className="flex items-center">
                    <div
                      className={`flex-shrink-0 p-3 rounded-lg ${area.color}`}
                    >
                      <area.icon
                        className="h-6 w-6 text-white"
                        aria-hidden="true"
                      />
                    </div>
                    <div className="ml-4 flex-1">
                      <h3 className="text-lg font-medium text-gray-900">
                        {area.title}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {area.description}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">{area.stats}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 최근 활동 */}
        <div className="lg:col-span-1">
          <h2 className="text-lg font-medium text-gray-900 mb-4">최근 활동</h2>
          <div className="bg-white shadow rounded-lg">
            <div className="p-6">
              <div className="flow-root">
                <ul className="-mb-8">
                  {recentActivities.map((activity, activityIdx) => (
                    <li key={activity.id}>
                      <div className="relative pb-8">
                        {activityIdx !== recentActivities.length - 1 ? (
                          <span
                            className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200"
                            aria-hidden="true"
                          />
                        ) : null}
                        <div className="relative flex space-x-3">
                          <div>
                            <span
                              className={`h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-white ${
                                activity.status === 'new'
                                  ? 'bg-blue-500'
                                  : 'bg-green-500'
                              }`}
                            >
                              <DocumentTextIcon className="h-4 w-4 text-white" />
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div>
                              <p className="text-sm text-gray-900">
                                {activity.message}
                              </p>
                              <p className="text-xs text-gray-500">
                                {activity.time}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
